import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { IconCircleCheck, IconLock, IconMail, IconMailCheck, IconMailX, IconUserPlus } from "@tabler/icons-react";
import { api, ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import type { Session } from "../lib/types";
import { tzs } from "../lib/format";
import { useI18n, type Lang } from "../i18n";
import { AuthLayout } from "../components/Layout";
import { ChannelSwitch, codeSentText, ResendRow, SendCodeButton, useOtpChannel, type OtpChannel } from "../components/Otp";
import {
  Alert,
  Button,
  ButtonLink,
  Checkbox,
  CodeInput,
  ErrorAlert,
  Input,
  PasswordInput,
  PhoneInput,
  Segmented,
  Spinner,
  useAction,
  useCooldown,
  useLoad,
} from "../components/ui";

const TZ_PHONE = /^\+255\d{9}$/;
export const PASSWORD_OK = (p: string) => p.length >= 8 && /[A-Za-z]/.test(p) && /\d/.test(p);
const EMAIL_OK = (e: string) => /^\S+@\S+\.\S+$/.test(e.trim());

/** Accepts 0712…, 712…, 255712… or +255712… and returns +255712…. */
export function normalizePhone(raw: string): string {
  const d = raw.replace(/[^\d+]/g, "");
  if (d.startsWith("+")) return d;
  if (d.startsWith("255")) return `+${d}`;
  if (d.startsWith("0")) return `+255${d.slice(1)}`;
  if (d.length === 9) return `+255${d}`;
  return d;
}

/** Shows a phone as "+255 712 345 678". */
export const prettyPhone = (p: string) => (/^\+255\d{9}$/.test(p) ? `+255 ${p.slice(4, 7)} ${p.slice(7, 10)} ${p.slice(10)}` : p);

/** Password rules ticked off as the agent types. */
function PasswordRules({ value }: { value: string }) {
  const { t } = useI18n();
  const rules = [
    { ok: value.length >= 8, label: t("pwRuleLength") },
    { ok: /[A-Za-z]/.test(value), label: t("pwRuleLetter") },
    { ok: /\d/.test(value), label: t("pwRuleNumber") },
  ];
  return (
    <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
      {rules.map((r) => (
        <li key={r.label} className={`flex items-center gap-1 ${r.ok ? "text-success" : "text-subtle"}`}>
          <IconCircleCheck size={14} className={r.ok ? "" : "opacity-40"} />
          {r.label}
        </li>
      ))}
    </ul>
  );
}

function AuthHeading({ title, body }: { title: string; body?: ReactNode }) {
  return (
    <div className="mb-6">
      <h1 className="text-[26px] font-semibold leading-tight tracking-tight text-ink sm:text-3xl">{title}</h1>
      {body && <p className="mt-2 text-sm leading-relaxed text-muted">{body}</p>}
    </div>
  );
}

/** A result screen (link sent, email verified…): icon, words, one action. */
function Result({ icon: Icon, tone, title, body, action }: { icon: typeof IconMail; tone: "success" | "error" | "info"; title: string; body?: ReactNode; action?: ReactNode }) {
  const ring = { success: "bg-success-soft text-success", error: "bg-danger-soft text-danger", info: "bg-info-soft text-info" }[tone];
  return (
    <div className="flex flex-col items-center py-6 text-center">
      <span className={`mb-5 flex h-16 w-16 items-center justify-center rounded-full ${ring}`}>
        <Icon size={32} />
      </span>
      <h1 className="text-2xl font-semibold text-ink">{title}</h1>
      {body && <div className="mt-2 max-w-sm text-sm text-muted">{body}</div>}
      {action && <div className="mt-6 w-full max-w-xs">{action}</div>}
    </div>
  );
}

export function LoginPage() {
  const { t } = useI18n();
  const { startSession } = useAuth();
  const navigate = useNavigate();
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const config = useLoad(() => api.get<{ agentMinCashout: number }>("/agent/public/config"));
  const login = useAction(async () => {
    const s = await api.post<Session>("/agent/auth/login", { phoneNumber: normalizePhone(phone), password });
    await startSession(s);
    navigate("/");
  });
  const noAccount = login.error instanceof ApiError && login.error.code === "no_account";
  return (
    <AuthLayout>
      {/* Phones don't get the side panel: a short pitch instead. */}
      <div className="relative mb-6 overflow-hidden rounded-3xl bg-primary p-5 text-white lg:hidden">
        <img src="/img/pattern.png" alt="" className="pointer-events-none absolute -right-16 -top-16 w-56 opacity-[0.12] mix-blend-screen" aria-hidden />
        <img src="/img/refer.png" alt="" className="absolute -bottom-2 right-1 h-32 w-auto drop-shadow-lg" />
        <p className="relative max-w-[62%] text-lg font-semibold leading-snug">{t("brandHeadline")}</p>
        <p className="relative mt-2 max-w-[60%] text-xs text-white/80">{t("upToPerDriver")}</p>
      </div>

      <AuthHeading title={t("signInTitle")} body={t("signInBody")} />
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void login.run();
        }}
      >
        <PhoneInput label={t("phone")} autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required />
        <div>
          <PasswordInput label={t("password")} autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          <div className="mt-2 text-right">
            <Link to="/forgot-password" className="text-sm font-medium text-brand hover:underline">
              {t("forgotPassword")}
            </Link>
          </div>
        </div>
        <ErrorAlert error={login.error} />
        {noAccount && (
          <ButtonLink to="/apply" variant="secondary" block>
            <IconUserPlus size={18} />
            {t("applyNow")}
          </ButtonLink>
        )}
        <Button type="submit" size="lg" block loading={login.loading} disabled={!phone || !password}>
          {t("signIn")}
        </Button>
      </form>

      <div className="mt-8 rounded-2xl border border-line bg-surface p-5">
        <p className="font-semibold text-ink">{t("noAccountYet")}</p>
        <p className="mt-1 text-sm text-muted">
          {t("applyPitch")}
          {config.data && ` ${t("minCashoutNote", { amount: tzs(config.data.agentMinCashout) })}`}
        </p>
        <ButtonLink to="/apply" variant="secondary" block className="mt-4">
          {t("applyNow")}
        </ButtonLink>
      </div>
      <p className="mt-6 text-center text-xs text-subtle">
        <Link to="/help" className="hover:text-ink">
          {t("navHelp")}
        </Link>
        {" · "}
        <Link to="/terms" className="hover:text-ink">
          {t("terms")}
        </Link>
      </p>
    </AuthLayout>
  );
}

/** Spec §5.1 step 1: one screen, the SMS code entered on the same screen. */
export function ApplyPage() {
  const { t, lang } = useI18n();
  const { startSession } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ name: "", phone: "", email: "", password: "", language: lang as Lang });
  const [pinId, setPinId] = useState<string | null>(null);
  const [otp, setOtp] = useState("");
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const cooldown = useCooldown(60);
  const phone = normalizePhone(f.phone);

  const [channel, setChannel] = useOtpChannel();
  const sendCode = useAction(async (ch: OtpChannel = channel) => {
    setChannel(ch);
    const r = await api.post<{ pinId: string }>("/agent/auth/otp", { phoneNumber: phone, channel: ch });
    setPinId(r.pinId);
    setOtp("");
    cooldown.start();
  });
  const apply = useAction(async () => {
    const s = await api.post<Session>("/agent/auth/apply", {
      name: f.name.trim(),
      phoneNumber: phone,
      email: f.email.trim() || null,
      password: f.password,
      language: f.language,
      pinId,
      otp: otp.trim(),
    });
    await startSession(s);
    navigate("/application");
  });
  const error = sendCode.error ?? apply.error;
  const signInInstead = error instanceof ApiError && error.code === "agent_exists";
  const errs = {
    name: f.name.trim().length < 2 ? t("errName") : null,
    phone: !TZ_PHONE.test(phone) ? t("errPhone") : null,
    // Optional: only checked when filled in.
    email: f.email.trim() && !EMAIL_OK(f.email) ? t("errEmail") : null,
    password: !PASSWORD_OK(f.password) ? t("passwordHint") : null,
  };
  const valid = !Object.values(errs).some(Boolean);
  const show = (k: keyof typeof errs) => (touched[k] ? errs[k] : null);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched({ name: true, phone: true, email: true, password: true });
    if (!valid) return;
    if (!pinId) void sendCode.run();
    else void apply.run();
  };

  return (
    <AuthLayout>
      <AuthHeading title={t("applyTitle")} body={t("applyBody")} />
      <ol className="mb-6 flex items-center gap-2 text-xs font-medium" aria-label={t("steps")}>
        {[t("stepDetails"), t("stepVerifyPhone")].map((label, i) => {
          const active = (pinId ? 1 : 0) === i;
          const done = Boolean(pinId) && i === 0;
          return (
            <li key={label} className="flex flex-1 items-center gap-2">
              <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${done ? "bg-success text-white dark:text-canvas" : active ? "bg-primary text-white" : "bg-sunken text-muted"}`}>{done ? "✓" : i + 1}</span>
              <span className={active ? "text-ink" : "text-muted"}>{label}</span>
              {i === 0 && <span className="h-px flex-1 bg-line-strong" aria-hidden />}
            </li>
          );
        })}
      </ol>
      <form className="flex flex-col gap-4" onSubmit={submit} noValidate>
        <fieldset disabled={Boolean(pinId)} className="flex flex-col gap-4 disabled:opacity-70">
          <Input label={t("fullName")} hint={t("fullNameHint")} autoComplete="name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} onBlur={() => setTouched({ ...touched, name: true })} error={show("name")} maxLength={100} />
          <PhoneInput label={t("phone")} hint={t("phoneVerifyHint")} autoComplete="tel" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} onBlur={() => setTouched({ ...touched, phone: true })} error={show("phone")} />
          <Input label={t("email")} optional hint={t("emailOptionalHint")} type="email" autoComplete="email" inputMode="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} onBlur={() => setTouched({ ...touched, email: true })} error={show("email")} />
          <div className="flex flex-col gap-2">
            <PasswordInput label={t("password")} autoComplete="new-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
            <PasswordRules value={f.password} />
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-ink">{t("language")}</span>
            <Segmented
              label={t("language")}
              value={f.language}
              onChange={(language) => setF({ ...f, language })}
              options={[
                { value: "sw", label: t("swahili") },
                { value: "en", label: t("english") },
              ]}
            />
            <p className="text-xs text-subtle">{t("languageHint")}</p>
          </div>
        </fieldset>

        {pinId && (
          <div className="flex flex-col gap-3 rounded-2xl border border-line bg-surface p-4">
            <p className="text-sm text-ink">{codeSentText(t, channel, prettyPhone(phone))}</p>
            <CodeInput label={t("smsCode")} value={otp} onChange={(e) => setOtp(e.target.value)} autoFocus />
            <ResendRow
              channel={channel}
              cooldownLeft={cooldown.left}
              loading={sendCode.loading}
              onResend={(ch) => void sendCode.run(ch)}
              leading={
                <button type="button" className="font-medium text-muted hover:text-ink" onClick={() => setPinId(null)}>
                  {t("editDetails")}
                </button>
              }
            />
          </div>
        )}
        <ErrorAlert error={error} />
        {signInInstead && (
          <ButtonLink to="/login" variant="secondary" block>
            {t("signIn")}
          </ButtonLink>
        )}
        {pinId ? (
          <Button type="submit" size="lg" block loading={apply.loading} disabled={otp.trim().length < 4}>
            {t("createAccount")}
          </Button>
        ) : (
          <>
            <SendCodeButton channel={channel} loading={sendCode.loading} />
            <ChannelSwitch channel={channel} onChange={setChannel} />
          </>
        )}
      </form>
      <p className="mt-6 text-center text-sm text-muted">
        {t("haveAccount")}{" "}
        <Link to="/login" className="font-semibold text-brand hover:underline">
          {t("signIn")}
        </Link>
      </p>
    </AuthLayout>
  );
}

/** Old verification links from emails sent before verification was dropped (2026-10-29). */
export function VerifyEmailPage() {
  const { t } = useI18n();
  const { signedIn } = useAuth();
  return (
    <AuthLayout>
      <Result icon={IconMailCheck} tone="success" title={t("verifyNotNeededTitle")} body={t("verifyNotNeededBody")} action={<ButtonLink to={signedIn ? "/" : "/login"} block>{t("continue")}</ButtonLink>} />
    </AuthLayout>
  );
}

/** SMS code to the phone by default (not every agent has an email); email link as the alternative. */
export function ForgotPasswordPage() {
  const { t } = useI18n();
  const [mode, setMode] = useState<"sms" | "email">("sms");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [pinId, setPinId] = useState<string | null>(null);
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState<"reset" | "linkSent" | null>(null);
  const cooldown = useCooldown(60);
  const normalized = normalizePhone(phone);

  const [channel, setChannel] = useOtpChannel();
  const sendCode = useAction(async (ch: OtpChannel = channel) => {
    setChannel(ch);
    setPinId((await api.post<{ pinId: string }>("/agent/auth/forgot-password/otp", { phoneNumber: normalized, channel: ch })).pinId);
    setOtp("");
    cooldown.start();
  });
  const reset = useAction(async () => {
    await api.post("/agent/auth/reset-password/otp", { phoneNumber: normalized, pinId, otp: otp.trim(), password });
    setDone("reset");
  });
  const sendLink = useAction(async () => {
    await api.post("/agent/auth/forgot-password", { identifier: email.trim() });
    setDone("linkSent");
  });

  if (done === "reset")
    return (
      <AuthLayout>
        <Result icon={IconCircleCheck} tone="success" title={t("passwordUpdated")} body={t("passwordReset")} action={<ButtonLink to="/login" block>{t("signIn")}</ButtonLink>} />
      </AuthLayout>
    );
  if (done === "linkSent")
    return (
      <AuthLayout>
        <Result icon={IconMail} tone="info" title={t("checkEmail")} body={t("linkSent")} action={<ButtonLink to="/login" variant="secondary" block>{t("backToSignIn")}</ButtonLink>} />
      </AuthLayout>
    );

  const mismatch = confirm.length > 0 && confirm !== password;
  return (
    <AuthLayout>
      <AuthHeading title={t("forgotTitle")} body={mode === "sms" ? t("forgotSmsBody") : t("forgotEmailBody")} />
      {mode === "sms" ? (
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (!pinId) void sendCode.run();
            else void reset.run();
          }}
        >
          <PhoneInput label={t("phone")} autoComplete="tel" value={phone} disabled={Boolean(pinId)} onChange={(e) => setPhone(e.target.value)} />
          {pinId && (
            <>
              <p className="text-sm text-ink">{codeSentText(t, channel, prettyPhone(normalized))}</p>
              <CodeInput label={t("smsCode")} value={otp} onChange={(e) => setOtp(e.target.value)} autoFocus />
              <ResendRow
                channel={channel}
                cooldownLeft={cooldown.left}
                loading={sendCode.loading}
                onResend={(ch) => void sendCode.run(ch)}
                leading={
                  <button type="button" className="font-medium text-muted hover:text-ink" onClick={() => setPinId(null)}>
                    {t("changeNumber")}
                  </button>
                }
              />
              <div className="flex flex-col gap-2">
                <PasswordInput label={t("newPassword")} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
                <PasswordRules value={password} />
              </div>
              <PasswordInput label={t("confirmPassword")} autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} error={mismatch ? t("passwordsDiffer") : null} />
            </>
          )}
          <ErrorAlert error={sendCode.error ?? reset.error} />
          {pinId ? (
            <Button type="submit" size="lg" block loading={reset.loading} disabled={otp.trim().length < 4 || !PASSWORD_OK(password) || password !== confirm}>
              {t("setPassword")}
            </Button>
          ) : (
            <>
              <SendCodeButton channel={channel} loading={sendCode.loading} disabled={!TZ_PHONE.test(normalized)} />
              <ChannelSwitch channel={channel} onChange={setChannel} />
            </>
          )}
          <button type="button" className="text-sm font-medium text-muted hover:text-ink" onClick={() => setMode("email")}>
            {t("useEmailInstead")}
          </button>
        </form>
      ) : (
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            void sendLink.run();
          }}
        >
          <Input label={t("email")} type="email" inputMode="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <ErrorAlert error={sendLink.error} />
          <Button type="submit" size="lg" block loading={sendLink.loading} disabled={!EMAIL_OK(email)}>
            {t("sendLink")}
          </Button>
          <button type="button" className="text-sm font-medium text-brand hover:underline" onClick={() => setMode("sms")}>
            {t("usePhoneCodeInstead")}
          </button>
        </form>
      )}
      <ButtonLink to="/login" variant="ghost" block className="mt-3">
        {t("backToSignIn")}
      </ButtonLink>
    </AuthLayout>
  );
}

export function ResetPasswordPage() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const reset = useAction(async () => {
    await api.post("/agent/auth/reset-password", { token: params.get("token") ?? "", password });
    setDone(true);
  });
  if (done)
    return (
      <AuthLayout>
        <Result icon={IconCircleCheck} tone="success" title={t("passwordUpdated")} body={t("passwordReset")} action={<ButtonLink to="/login" block>{t("signIn")}</ButtonLink>} />
      </AuthLayout>
    );
  const mismatch = confirm.length > 0 && confirm !== password;
  return (
    <AuthLayout>
      <AuthHeading title={t("resetTitle")} body={t("resetBody")} />
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void reset.run();
        }}
      >
        <div className="flex flex-col gap-2">
          <PasswordInput label={t("newPassword")} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          <PasswordRules value={password} />
        </div>
        <PasswordInput label={t("confirmPassword")} autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} error={mismatch ? t("passwordsDiffer") : null} required />
        <ErrorAlert error={reset.error} />
        <Button type="submit" size="lg" block loading={reset.loading} disabled={!PASSWORD_OK(password) || password !== confirm}>
          <IconLock size={18} />
          {t("setPassword")}
        </Button>
      </form>
    </AuthLayout>
  );
}

/** Admin-created agents (spec §5.6): verify phone, set password, accept terms. */
export function InvitePage() {
  const { t } = useI18n();
  const { startSession } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [info, setInfo] = useState<{ name: string; phoneMasked: string } | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [pinId, setPinId] = useState<string | null>(null);
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [terms, setTerms] = useState(false);
  const [consent, setConsent] = useState(false);
  const cooldown = useCooldown(60);

  useEffect(() => {
    api.post<{ name: string; phoneMasked: string }>("/agent/auth/invite/check", { token }).then(setInfo).catch(setLoadError);
  }, [token]);

  const [channel, setChannel] = useOtpChannel();
  const sendCode = useAction(async (ch: OtpChannel = channel) => {
    setChannel(ch);
    setPinId((await api.post<{ pinId: string }>("/agent/auth/invite/otp", { token, channel: ch })).pinId);
    cooldown.start();
  });
  const accept = useAction(async () => {
    const s = await api.post<Session>("/agent/auth/invite/accept", { token, pinId, otp: otp.trim(), password, acceptTerms: terms, acceptConsent: consent });
    await startSession(s);
    navigate("/");
  });

  if (loadError)
    return (
      <AuthLayout>
        <Result icon={IconMailX} tone="error" title={t("inviteInvalid")} body={<ErrorAlert error={loadError} />} action={<ButtonLink to="/help" variant="secondary" block>{t("contactSupport")}</ButtonLink>} />
      </AuthLayout>
    );
  return (
    <AuthLayout>
      {!info ? (
        <Spinner />
      ) : (
        <>
          <AuthHeading title={t("inviteTitle")} body={t("inviteBody", { name: info.name, phone: info.phoneMasked })} />
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (!pinId) void sendCode.run();
              else void accept.run();
            }}
          >
            {pinId && (
              <>
                <Alert tone="info">{codeSentText(t, channel, info.phoneMasked)}</Alert>
                <CodeInput label={t("smsCode")} value={otp} onChange={(e) => setOtp(e.target.value)} autoFocus />
                <ResendRow channel={channel} cooldownLeft={cooldown.left} loading={sendCode.loading} onResend={(ch) => void sendCode.run(ch)} />
                <div className="flex flex-col gap-2">
                  <PasswordInput label={t("password")} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
                  <PasswordRules value={password} />
                </div>
                <div className="flex flex-col gap-2 rounded-2xl border border-line bg-surface p-3">
                  <Checkbox checked={terms} onChange={setTerms}>
                    {t("acceptTerms")} ·{" "}
                    <Link to="/terms" target="_blank" className="font-medium text-brand underline">
                      {t("readTerms")}
                    </Link>
                  </Checkbox>
                  <Checkbox checked={consent} onChange={setConsent}>
                    {t("acceptConsent")}
                  </Checkbox>
                </div>
              </>
            )}
            <ErrorAlert error={sendCode.error ?? accept.error} />
            {pinId ? (
              <Button type="submit" size="lg" block loading={accept.loading} disabled={!terms || !consent || !PASSWORD_OK(password) || otp.trim().length < 4}>
                {t("finishSetup")}
              </Button>
            ) : (
              <>
                <SendCodeButton channel={channel} loading={sendCode.loading} />
                <ChannelSwitch channel={channel} onChange={setChannel} />
              </>
            )}
          </form>
        </>
      )}
    </AuthLayout>
  );
}
